/**
 * Hook zum Verwalten des allgemeinen Status der CalendarView.
 * Extrahiert aus der Hauptkomponente zur besseren Lesbarkeit.
 * 
 * @param {Object} props
 * @param {number} props.viewDays - Anzahl der Tage in der aktuellen Ansicht.
 * @param {string} props.viewMode - 'month' oder 'days'.
 * @returns {Object} Zustände, Refs und berechnete Dimensionen (actualAlldayWidth, dynamicHourWidth, etc.).
 */
import { useState, useEffect, useRef, useMemo } from 'react';

export function useCalendarState({ viewDays, viewMode }) {
    // DOM Refs für Scrolling und Sizing
    const scrollRef = useRef(null);
    const containerRef = useRef(null);

    // Filter und Ansichts-States
    const [showRangePicker, setShowRangePicker] = useState(false);
    
    // UI Konstanten und Settings (LocalStorage)
    const [isAllDayCollapsed, setIsAllDayCollapsed] = useState(() => {
        const saved = sessionStorage.getItem('leader_allday_collapsed');
        return saved === 'true';
    });
    const [hourStart, setHourStart] = useState(() => {
        const saved = sessionStorage.getItem('leader_hour_start');
        return saved ? parseInt(saved, 10) : 7;
    });
    const [hourEnd, setHourEnd] = useState(() => {
        const saved = sessionStorage.getItem('leader_hour_end');
        return saved ? parseInt(saved, 10) : 16;
    });
    const [alldayWidth, setAlldayWidth] = useState(() => {
        const saved = sessionStorage.getItem('leader_allday_width');
        return saved ? parseInt(saved, 10) : 120;
    });

    // Lokaler State speichern
    useEffect(() => {
        sessionStorage.setItem('leader_allday_width', alldayWidth);
    }, [alldayWidth]);

    useEffect(() => {
        sessionStorage.setItem('leader_allday_collapsed', isAllDayCollapsed);
    }, [isAllDayCollapsed]);

    useEffect(() => {
        sessionStorage.setItem('leader_hour_start', hourStart);
        sessionStorage.setItem('leader_hour_end', hourEnd);
    }, [hourStart, hourEnd]);

    // Resizing Interaktionen
    const [resizingAllday, setResizingAllday] = useState(false);
    const [hoveringAlldayResizer, setHoveringAlldayResizer] = useState(false);
    const [interactionEnded, setInteractionEnded] = useState(false);
    const [hasManuallyResized, setHasManuallyResized] = useState(false);

    // Dimensionen der Darstellung
    const [dynamicRowHeight, setDynamicRowHeight] = useState(100);
    const [containerWidth, setContainerWidth] = useState(800);
    const [scrollX, setScrollX] = useState(0);

    // Track horizontal & vertical scroll for content stickiness & persistence
    useEffect(() => {
        const el = scrollRef.current;
        if (!el || viewMode !== 'days') return;
        
        const handleScroll = () => {
            setScrollX(el.scrollLeft);
            sessionStorage.setItem('leader_calendar_scroll_x', el.scrollLeft);
        };

        el.addEventListener('scroll', handleScroll, { passive: true });
        
        // Initial sync of scrollX state
        setScrollX(el.scrollLeft);
        
        return () => el.removeEventListener('scroll', handleScroll);
    }, [viewMode]);

    // Track container height and width
    useEffect(() => {
        if (!containerRef.current) return;
        
        const calculateHeights = (rect) => {
            // Header is 47px, Footer is 17px (16px + 1px border)
            // We use a slightly larger buffer (70px) to prevent sub-pixel scrolling
            const reserved = 70;
            const availableHeight = Math.max(0, rect.height - reserved);
            const daysOnScreen = Math.min(viewDays, 7);
            const calcHeight = Math.max(80, availableHeight / daysOnScreen);
            setDynamicRowHeight(calcHeight);
            setContainerWidth(rect.width);
        };

        // Initial check
        if (containerRef.current) {
            calculateHeights(containerRef.current.getBoundingClientRect());
        }

        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                // Use getBoundingClientRect for the most accurate layout-box size
                calculateHeights(entry.target.getBoundingClientRect());
            }
        });
        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, [viewDays, viewMode]);

    // 'Jetzt' Timer (Aktuelle Uhrzeit-Linie)
    const [now, setNow] = useState(new Date());
    useEffect(() => {
        const timer = setInterval(() => setNow(new Date()), 60000);
        return () => clearInterval(timer);
    }, []);

    // Abgeleitete Werte (Memoized)
    const actualAlldayWidth = isAllDayCollapsed ? 40 : alldayWidth;
    const GRID_BUFFER = 40; // Horizontal padding for labels at edges

    const dynamicHourWidth = useMemo(() => {
        const MIN_HOUR_WIDTH = 120;
        const available = containerWidth - 80 - actualAlldayWidth - (2 * GRID_BUFFER);
        const hours = hourEnd - hourStart;
        if (hours <= 0) return MIN_HOUR_WIDTH;
        
        // Either stretch to fill or use minimum
        const width = Math.max(MIN_HOUR_WIDTH, available / hours);
        // Floor to prevent fractional pixel overflow that triggers unwanted scrolling
        return Math.floor(width * 100) / 100;
    }, [containerWidth, actualAlldayWidth, hourStart, hourEnd]);

    const nowHour = now.getHours() + now.getMinutes() / 60;
    const nowLeft = GRID_BUFFER + (nowHour - hourStart) * dynamicHourWidth;
    const TIMELINE_WIDTH = ((hourEnd - hourStart) * dynamicHourWidth) + (2 * GRID_BUFFER);

    return {
        // Refs
        scrollRef,
        containerRef,
        // States
        showRangePicker, setShowRangePicker,
        isAllDayCollapsed, setIsAllDayCollapsed,
        hourStart, setHourStart,
        hourEnd, setHourEnd,
        alldayWidth, setAlldayWidth,
        resizingAllday, setResizingAllday,
        hoveringAlldayResizer, setHoveringAlldayResizer,
        interactionEnded, setInteractionEnded,
        hasManuallyResized, setHasManuallyResized,
        dynamicRowHeight,
        containerWidth,
        scrollX,
        now,
        nowHour,
        // Abgeleitet
        actualAlldayWidth,
        GRID_BUFFER,
        dynamicHourWidth,
        nowLeft,
        TIMELINE_WIDTH
    };
}
